import {
  DataStatus,
  FormField,
  Form,
  FormSubmitHandler,
  ButtonGroup,
  FormButton,
  Separator,
} from 'platform/components';
import {Grid, Heading, Show, Text, VStack} from 'platform/foundation';

import {
  useGetServiceCaseIntegrationsEvaClaimAdditionalInformationQuery,
  usePatchServiceCaseIntegrationsEvaClaimAdditionalInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {parseDate} from 'shared/web';

import {
  EvaClaimAdditionalInformationFormSchema,
  EvaClaimAdditionalInformationFormType,
} from '../types/EvaClaimAdditionalInformationFormType';

interface OrderEvaClaimInformationProps {
  orderId: string;
  serviceCaseId: string;
}

export function OrderEvaClaimInformation(props: OrderEvaClaimInformationProps) {
  const {
    data: serviceCaseIntegrationsEvaClaimAdditionalInformation,
    isError: isServiceCaseIntegrationsEvaClaimAdditionalInformationError,
    isFetching: isServiceCaseIntegrationsEvaClaimAdditionalInformationFetching,
  } = useGetServiceCaseIntegrationsEvaClaimAdditionalInformationQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.orderId,
  });
  const [patchServiceCaseIntegrationsEvaClaimAdditionalInformation] =
    usePatchServiceCaseIntegrationsEvaClaimAdditionalInformationMutation();

  const handleSubmit: FormSubmitHandler<EvaClaimAdditionalInformationFormType> = async (data) =>
    await patchServiceCaseIntegrationsEvaClaimAdditionalInformation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      body: {
        damageCode: data.damageCode ?? undefined,
        isPartClaimDamage: data.isPartClaimDamage ?? undefined,
        partClaimData: data.isPartClaimDamage
          ? {
              installationDate: data.installationDate ?? null,
              installationMileage: data.installationMileage ?? null,
              replacedByDealer: data.replacedByDealer ?? null,
              replacedPartNumber: data.replacedPartNumber ?? null,
            }
          : undefined,
      },
    })
      .unwrap()
      .catch(handleApiError);

  const defaultValues: EvaClaimAdditionalInformationFormType = {
    damageCode: serviceCaseIntegrationsEvaClaimAdditionalInformation?.damageCode ?? undefined,
    isPartClaimDamage:
      serviceCaseIntegrationsEvaClaimAdditionalInformation?.isPartClaimDamage ?? false,
    installationDate: serviceCaseIntegrationsEvaClaimAdditionalInformation?.partClaimData
      ?.installationDate
      ? parseDate(
          serviceCaseIntegrationsEvaClaimAdditionalInformation?.partClaimData?.installationDate
        )
      : undefined,
    installationMileage:
      serviceCaseIntegrationsEvaClaimAdditionalInformation?.partClaimData?.installationMileage ??
      undefined,
    replacedByDealer:
      serviceCaseIntegrationsEvaClaimAdditionalInformation?.partClaimData?.replacedByDealer ??
      undefined,
    replacedPartNumber:
      serviceCaseIntegrationsEvaClaimAdditionalInformation?.partClaimData?.replacedPartNumber ??
      undefined,
  };

  return (
    <Show when={!isServiceCaseIntegrationsEvaClaimAdditionalInformationError}>
      <DataStatus
        isLoading={isServiceCaseIntegrationsEvaClaimAdditionalInformationFetching}
        isError={isServiceCaseIntegrationsEvaClaimAdditionalInformationError}
      >
        <VStack spacing={1}>
          <Heading size={4}>{i18n.t('entity.serviceOrder.labels.evaClaimIntegration')}</Heading>
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.serviceOrder.labels.evaClaimIntegrationDescription')}
          </Text>
        </VStack>
        <Separator />
        <Form<EvaClaimAdditionalInformationFormType>
          onSubmit={handleSubmit}
          experimentalZodSchema={EvaClaimAdditionalInformationFormSchema}
          defaultValues={defaultValues}
        >
          {(control, formApi) => {
            const isPartClaimDamage = formApi.watch('isPartClaimDamage');
            return (
              <VStack spacing={4}>
                <Grid columns={4} spacing={4}>
                  <FormField
                    control={control}
                    name="damageCode"
                    type="text"
                    label={i18n.t('entity.serviceOrder.labels.damageCode')}
                    maxLength={7}
                    isCounterVisible
                  />
                </Grid>
                <FormField
                  control={control}
                  name="isPartClaimDamage"
                  type="switch"
                  label={i18n.t('entity.serviceOrder.labels.isPartClaimDamage')}
                />
                <Show when={isPartClaimDamage}>
                  <Grid columns={4} spacing={4}>
                    <FormField
                      control={control}
                      name="installationDate"
                      type="date"
                      label={i18n.t('entity.serviceOrder.labels.installationDate')}
                      isRequired
                    />
                    <FormField
                      control={control}
                      name="installationMileage"
                      type="number"
                      label={i18n.t('entity.serviceOrder.labels.installationMileage')}
                      suffix={i18n.t('general.metric.km')}
                      isRequired
                    />
                    <FormField
                      control={control}
                      name="replacedByDealer"
                      type="text"
                      label={i18n.t('entity.serviceOrder.labels.replacedByDealer')}
                      isRequired
                    />
                    <FormField
                      control={control}
                      name="replacedPartNumber"
                      type="text"
                      label={i18n.t('entity.serviceOrder.labels.replacedPartNumber')}
                      isRequired
                    />
                  </Grid>
                </Show>
                <ButtonGroup align="right">
                  <FormButton
                    control={control}
                    type="submit"
                    title={i18n.t('general.actions.saveChanges')}
                  />
                </ButtonGroup>
              </VStack>
            );
          }}
        </Form>
      </DataStatus>
    </Show>
  );
}
